import * as React from 'react';
import {Button} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import {FileViewerHeaderLabel} from './FileViewerHeaderLabel';
import {ItemType} from './FileViewer';
import {GridViewer} from './GridViewer';
import {intersection} from 'lodash';
import {StudioFileItemGroup} from '.';
import {StudioDatasetViewerProps} from '../StudioDatasetViewer';
import './LabelView.scss';

export type GroupKey = 'type' | 'label' | 'binaryType';
export const NO_GROUP = 'no_group';

export type LabelViewerProps = {
  labels?: StudioFileItemGroup[];
  labelNameMapper(label: string): string;
  limit: number;
  selections?: string[];
  onLabelClick(id: string | number | undefined, label: string): void;
  onSelectAll?(value: boolean, ids: string[]): void;
  children: (label: string, file: ItemType) => React.ReactNode;
} & Pick<StudioDatasetViewerProps, 'allowSelection'>;

export const LabelView = ({
  labels = [],
  labelNameMapper,
  limit,
  selections = [],
  onLabelClick = () => {},
  onSelectAll = () => {},
  allowSelection = true,
  children,
}: LabelViewerProps) => {
  const intl = useIntl();

  return (
    <div className="label-viewer">
      {labels.map((data, dataLabelIndex) => {
        const files = data.list;
        const remaining = data.totalElements - files.length;
        const fileIds = files.map(file => file.id);
        const groupSelections = intersection(fileIds, selections);
        const header =
          data.label === NO_GROUP
            ? intl.formatMessage({
                id: 'fileViewer.unlabeled',
              })
            : labelNameMapper(data?.label || '');
        return (
          <div
            key={`label-data-${data.label}-index-${dataLabelIndex}`}
            className="label-viewer__row"
          >
            <FileViewerHeaderLabel
              label={header}
              onClick={() => onLabelClick(data.label, header)}
              count={data.totalElements}
              selected={groupSelections.length}
              showSelector={allowSelection}
              onSelectAll={val =>
                onSelectAll(
                  val,
                  files.map(file => file.id)
                )
              }
              checked={!!groupSelections.length}
              indeterminate={
                !!groupSelections.length && files.length !== groupSelections.length
              }
            />
            <GridViewer selections={selections}>
              {files.map((file, fileIndex) => (
                <div key={`file-${file.id}-index-${fileIndex}`}>
                  {children(header, file)}
                </div>
              ))}
            </GridViewer>
            {Boolean(limit) && (
              <Button
                onClick={() => onLabelClick(data.label, header)}
                color="primary"
                className="label-viewer__view-all"
              >
                {remaining <= 0 ? (
                  <FormattedMessage
                    id="dataViewer.viewOnly"
                    values={{
                      label: header,
                    }}
                  />
                ) : (
                  <FormattedMessage id="dataViewer.viewMore" values={{remaining}} />
                )}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};
